import { read, utils, writeXLSX, WorkBook, WorkSheet } from "@sheet/core";
import { ImportRejectionRowProblem } from "@common/schemas/domains/accounting/import-rejection.schema";
import { setBackgroundColor } from "./sheet";

export const FLAGGED_XLSX_ROW_FILL = "#FFE08A";
export const FLAGGED_XLSX_PROBLEM_COLUMN_HEADER = "Problem";

interface HighlightXlsxRowsParams {
    workbookBytes: ArrayBuffer | Uint8Array;
    rowNumbers: number[];
    rowProblems?: ImportRejectionRowProblem[];
    sheetName?: string;
    fill?: string;
}

interface HighlightWorksheetRowsParams {
    worksheet: WorkSheet;
    rowNumbers: number[];
    rowProblems: ImportRejectionRowProblem[];
    fill: string;
}

interface VisibleSheet {
    name: string;
    worksheet: WorkSheet;
}

export function highlightXlsxRows({
    workbookBytes,
    rowNumbers,
    rowProblems = [],
    sheetName,
    fill = FLAGGED_XLSX_ROW_FILL,
}: HighlightXlsxRowsParams): Uint8Array {
    const bytes =
        workbookBytes instanceof ArrayBuffer
            ? new Uint8Array(workbookBytes)
            : workbookBytes;
    const workbook = read(bytes, { type: "array", cellStyles: true });
    const flaggedRowNumbers = rowNumbers.filter((rowNumber) => rowNumber >= 1);
    const worksheet = worksheetToHighlight({ workbook, sheetName });
    if (worksheet && flaggedRowNumbers.length > 0) {
        highlightWorksheetRows({
            worksheet,
            rowNumbers: flaggedRowNumbers,
            rowProblems,
            fill,
        });
    }
    return workbookWriteBytes(workbook);
}

function visibleSheets(workbook: WorkBook): VisibleSheet[] {
    const sheets: VisibleSheet[] = [];
    for (const [index, name] of workbook.SheetNames.entries()) {
        const sheetMeta = workbook.Workbook?.Sheets?.[index];
        if (sheetMeta?.Hidden) {
            continue;
        }
        const worksheet = workbook.Sheets[name];
        if (worksheet) {
            sheets.push({ name, worksheet });
        }
    }
    return sheets;
}

function worksheetArea(worksheet: WorkSheet): number {
    const ref = worksheet["!ref"];
    if (!ref) {
        return 0;
    }
    const range = utils.decode_range(ref);
    return (range.e.r - range.s.r + 1) * (range.e.c - range.s.c + 1);
}

interface WorksheetToHighlightParams {
    workbook: WorkBook;
    sheetName?: string;
}

function worksheetToHighlight({
    workbook,
    sheetName,
}: WorksheetToHighlightParams): WorkSheet | undefined {
    const sheets = visibleSheets(workbook);
    if (sheetName) {
        const namedSheet = sheets.find((sheet) => sheet.name === sheetName);
        if (namedSheet) {
            return namedSheet.worksheet;
        }
    }
    let largest: VisibleSheet | undefined;
    for (const sheet of sheets) {
        if (
            !largest ||
            worksheetArea(sheet.worksheet) > worksheetArea(largest.worksheet)
        ) {
            largest = sheet;
        }
    }
    return largest?.worksheet;
}

function highlightWorksheetRows({
    worksheet,
    rowNumbers,
    rowProblems,
    fill,
}: HighlightWorksheetRowsParams): void {
    const ref = worksheet["!ref"];
    if (!ref || rowNumbers.length === 0) {
        return;
    }
    const range = utils.decode_range(ref);
    const inRangeRowNumbers = rowNumbers.filter((rowNumber) => {
        const rowIndex = rowNumber - 1;
        return rowIndex >= range.s.r && rowIndex <= range.e.r;
    });
    if (inRangeRowNumbers.length === 0) {
        return;
    }
    const problemColumn = range.e.c + 1;
    range.e.c = problemColumn;
    worksheet["!ref"] = utils.encode_range(range);
    const flaggedRowNumbers = new Set(inRangeRowNumbers);
    if (!flaggedRowNumbers.has(range.s.r + 1)) {
        const headerAddress = utils.encode_cell({
            r: range.s.r,
            c: problemColumn,
        });
        worksheet[headerAddress] = {
            t: "s",
            v: FLAGGED_XLSX_PROBLEM_COLUMN_HEADER,
        };
    }
    const problemByRow = new Map(
        rowProblems.map((rowProblem) => [
            rowProblem.rowNumber,
            rowProblem.problem,
        ]),
    );
    const columnIndices = Array.from(
        { length: range.e.c - range.s.c + 1 },
        (_, offset) => range.s.c + offset,
    );
    for (const rowNumber of inRangeRowNumbers) {
        const rowIndex = rowNumber - 1;
        const problemAddress = utils.encode_cell({
            r: rowIndex,
            c: problemColumn,
        });
        worksheet[problemAddress] = {
            t: "s",
            v: problemByRow.get(rowNumber) ?? "",
        };
        for (const columnIndex of columnIndices) {
            const address = utils.encode_cell({ r: rowIndex, c: columnIndex });
            if (!worksheet[address]) {
                worksheet[address] = { t: "s", v: "" };
            }
            setBackgroundColor(worksheet, address, fill);
        }
    }
}

function workbookWriteBytes(workbook: WorkBook): Uint8Array {
    const output = writeXLSX(workbook, { type: "array", cellStyles: true });
    if (output instanceof ArrayBuffer) {
        return new Uint8Array(output);
    }
    if (output instanceof Uint8Array) {
        return output;
    }
    if (Array.isArray(output)) {
        return Uint8Array.from(
            output.filter((byte) => typeof byte === "number"),
        );
    }
    throw new Error("Failed to write highlighted spreadsheet");
}
